import React, { useEffect, useState } from "react";
import { MessageCircle, Send, Users } from "lucide-react";

import {
  deleteBaleBotSubscriber,
  getBaleBotConfig,
  listBaleBotSubscribers,
  sendBaleBotTestMessage,
  updateBaleBotConfig,
  updateBaleBotSubscriber,
} from "@/api/baleBot";
import { Badge } from "@/components/primitives/Badge";
import { Button } from "@/components/primitives/Button";
import { Header } from "@/components/layout/Header";
import { Input } from "@/components/primitives/Input";
import { PageShell } from "@/components/layout/PageShell";
import { TextArea } from "@/components/primitives/TextArea";
import { BaleBotConfig, BaleBotSubscriber, Paginated } from "@/types";

const DEFAULT_VARIABLES =
  "Available variables: {flow_name}, {step_name}, {url}, {method}, {expected_status}, {actual_status}, {error_message}, {response_body}, {started_at}";

export const BaleBotPage: React.FC = () => {
  const [config, setConfig] = useState<BaleBotConfig | null>(null);
  const [subscribers, setSubscribers] = useState<Paginated<BaleBotSubscriber>>({
    items: [],
    total: 0,
    limit: 50,
    offset: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [testChatId, setTestChatId] = useState("");
  const [testText, setTestText] = useState("");

  const fetchData = async (offset = 0) => {
    setIsLoading(true);
    setError(null);
    try {
      const [cfg, subs] = await Promise.all([
        getBaleBotConfig(),
        listBaleBotSubscribers({ limit: 50, offset }),
      ]);
      setConfig(cfg);
      setSubscribers(subs);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load Bale bot data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleConfigChange = <K extends keyof BaleBotConfig>(
    key: K,
    value: BaleBotConfig[K],
  ) => {
    setConfig((prev) => (prev ? { ...prev, [key]: value } : prev));
    setSaveMessage(null);
  };

  const handleSaveConfig = async () => {
    if (!config) return;
    setSaveMessage(null);
    try {
      const updated = await updateBaleBotConfig({
        is_enabled: config.is_enabled,
        bot_token: config.bot_token,
        api_base_url: config.api_base_url,
        password: config.password,
        welcome_text: config.welcome_text,
        password_prompt_text: config.password_prompt_text,
        success_text: config.success_text,
        default_message_template: config.default_message_template,
      });
      setConfig(updated);
      setSaveMessage("Configuration saved successfully.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save configuration");
    }
  };

  const handleToggleSubscriber = async (subscriber: BaleBotSubscriber) => {
    try {
      const updated = await updateBaleBotSubscriber(subscriber.id, {
        is_authorized: !subscriber.is_authorized,
      });
      setSubscribers((prev) => ({
        ...prev,
        items: prev.items.map((s) => (s.id === updated.id ? updated : s)),
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update subscriber");
    }
  };

  const handleDeleteSubscriber = async (id: string) => {
    if (!window.confirm("Are you sure you want to remove this subscriber?")) return;
    try {
      await deleteBaleBotSubscriber(id);
      setSubscribers((prev) => ({
        ...prev,
        items: prev.items.filter((s) => s.id !== id),
        total: prev.total - 1,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete subscriber");
    }
  };

  const handleSendTest = async () => {
    try {
      await sendBaleBotTestMessage(testChatId.trim() || null, testText.trim() || null);
      setSaveMessage("Test message sent.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send test message");
    }
  };

  return (
    <PageShell>
      <Header
        action={
          <Button variant="secondary" onClick={() => (window.location.href = "/")}>
            Flows
          </Button>
        }
      />

      <main className="page-container py-10">
        <div className="mb-8">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
            Bale Bot
          </h2>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Configure the bot and manage subscribers.
          </p>
        </div>

        {isLoading && (
          <div className="py-20 text-center text-slate-500 dark:text-slate-400">Loading…</div>
        )}
        {error && (
          <div
            className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
            role="alert"
          >
            {error}
          </div>
        )}
        {saveMessage && (
          <div
            className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
            role="status"
          >
            {saveMessage}
          </div>
        )}

        {!isLoading && config && (
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-6">
              <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-softnu-50 text-softnu-600 dark:bg-softnu-900/30 dark:text-softnu-300">
                      <MessageCircle className="h-5 w-5" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                      Configuration
                    </h3>
                  </div>
                  <Badge variant={config.is_enabled ? "success" : "neutral"}>
                    {config.is_enabled ? "Enabled" : "Disabled"}
                  </Badge>
                </div>

                <label className="mb-5 flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    checked={config.is_enabled}
                    onChange={(e) => handleConfigChange("is_enabled", e.target.checked)}
                    className="h-5 w-5 rounded border-slate-300 text-softnu-600 focus:ring-softnu-500 dark:border-slate-700 dark:bg-slate-800"
                  />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    Enable Bale bot
                  </span>
                </label>

                <div className="space-y-4">
                  <Input
                    label="Bot token"
                    type="password"
                    value={config.bot_token ?? ""}
                    onChange={(e) => handleConfigChange("bot_token", e.target.value || null)}
                    placeholder="123456:ABC-DEF..."
                  />
                  <Input
                    label="API base URL"
                    value={config.api_base_url}
                    onChange={(e) => handleConfigChange("api_base_url", e.target.value)}
                    placeholder="https://tapi.bale.ai"
                  />
                  <Input
                    label="Authorization password"
                    type="password"
                    value={config.password ?? ""}
                    onChange={(e) => handleConfigChange("password", e.target.value || null)}
                    placeholder="Users must send this after /start"
                  />
                  <TextArea
                    label="Welcome text"
                    value={config.welcome_text ?? ""}
                    onChange={(e) => handleConfigChange("welcome_text", e.target.value || null)}
                    rows={2}
                  />
                  <TextArea
                    label="Password prompt text"
                    value={config.password_prompt_text ?? ""}
                    onChange={(e) =>
                      handleConfigChange("password_prompt_text", e.target.value || null)
                    }
                    rows={2}
                  />
                  <TextArea
                    label="Success text"
                    value={config.success_text ?? ""}
                    onChange={(e) => handleConfigChange("success_text", e.target.value || null)}
                    rows={2}
                  />
                  <TextArea
                    label="Default failure message template"
                    value={config.default_message_template ?? ""}
                    onChange={(e) =>
                      handleConfigChange("default_message_template", e.target.value || null)
                    }
                    placeholder={DEFAULT_VARIABLES}
                    rows={6}
                  />
                </div>

                <div className="mt-6 flex justify-end">
                  <Button onClick={handleSaveConfig}>Save configuration</Button>
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="mb-6 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-softnu-50 text-softnu-600 dark:bg-softnu-900/30 dark:text-softnu-300">
                    <Send className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Send test message
                  </h3>
                </div>

                <div className="space-y-4">
                  <Input
                    label="Chat ID (leave empty to use the first authorized subscriber)"
                    value={testChatId}
                    onChange={(e) => setTestChatId(e.target.value)}
                    placeholder="123456789"
                  />
                  <TextArea
                    label="Message text"
                    value={testText}
                    onChange={(e) => setTestText(e.target.value)}
                    placeholder="Hello from Service Health Monitor"
                    rows={3}
                  />
                </div>

                <div className="mt-6 flex justify-end">
                  <Button variant="secondary" onClick={handleSendTest} className="gap-1.5">
                    <Send className="h-4 w-4" />
                    Send test
                  </Button>
                </div>
              </section>
            </div>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 lg:col-span-1">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-softnu-50 text-softnu-600 dark:bg-softnu-900/30 dark:text-softnu-300">
                  <Users className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Subscribers</h3>
              </div>

              {subscribers.items.length === 0 ? (
                <p className="py-10 text-center text-slate-500 dark:text-slate-400">
                  No subscribers yet.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
                      <tr>
                        <th className="px-4 py-3 font-medium">Chat ID</th>
                        <th className="px-4 py-3 font-medium">Username</th>
                        <th className="px-4 py-3 font-medium">Name</th>
                        <th className="px-4 py-3 font-medium">Authorized</th>
                        <th className="px-4 py-3 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {subscribers.items.map((subscriber) => (
                        <tr
                          key={subscriber.id}
                          className="transition-colors hover:bg-slate-50/50 dark:hover:bg-slate-800/30"
                        >
                          <td className="px-4 py-3 font-mono text-slate-900 dark:text-white">
                            {subscriber.chat_id}
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                            {subscriber.username ?? "—"}
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                            {[subscriber.first_name, subscriber.last_name]
                              .filter(Boolean)
                              .join(" ") || "—"}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={subscriber.is_authorized ? "success" : "warning"}>
                              {subscriber.is_authorized ? "Yes" : "No"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex flex-col items-end gap-2 sm:flex-row sm:justify-end">
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => handleToggleSubscriber(subscriber)}
                              >
                                {subscriber.is_authorized ? "Revoke" : "Authorize"}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteSubscriber(subscriber.id)}
                                className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/30"
                              >
                                Remove
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {subscribers.total > 50 && (
                <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4 dark:border-slate-800">
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    {subscribers.offset + 1}–
                    {Math.min(subscribers.offset + subscribers.limit, subscribers.total)} of{" "}
                    {subscribers.total}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => fetchData(Math.max(0, subscribers.offset - subscribers.limit))}
                      disabled={subscribers.offset === 0 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => fetchData(subscribers.offset + subscribers.limit)}
                      disabled={subscribers.offset + subscribers.limit >= subscribers.total || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </PageShell>
  );
};
